import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Fragment, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { CheckboxField, SelectField, TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { adminApi, roleInput, type Role, type RoleInput } from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { PHONE_QUERY, useMediaQuery } from "@/lib/use-media-query";

import { ScopeRulesEditor } from "./ScopeRulesEditor";

// A new role sees every queue until narrowed down: a role without any queue rule sees no ticket at all,
// which is rarely what the administrator meant.
const EMPTY_ROLE: RoleInput = {
  name: "",
  description: "",
  require_2fa: false,
  permissions: [],
  scope_rules: [{ queue_id: null, include_subqueues: true, customer: null, date_from: null, date_to: null }],
};

function roleError(error: unknown, t: (key: string) => string): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.code) {
    const key = `admin.roles.errors.${error.code}`;
    const text = t(key);
    if (text !== key) return text;
  }
  return t("common.errorGeneric");
}

const COMPARED_BY_DEFAULT = 4;

function PermissionMatrix({ roles }: { roles: Role[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const catalog = useQuery({ queryKey: ["admin", "permissions"], queryFn: adminApi.permissions });
  const [changes, setChanges] = useState<Record<number, Set<string>>>({});
  const [saved, setSaved] = useState(false);
  // Phones show one role at a time: a column per role does not fit.
  const phone = useMediaQuery(PHONE_QUERY);
  const [shownRoleId, setShownRoleId] = useState<number | null>(null);
  const shownRole =
    roles.find((role) => role.id === shownRoleId) ?? roles.find((role) => !role.is_system) ?? roles[0];
  // Desktop compares a chosen few: a column per role does not scale to many roles.
  const [compared, setCompared] = useState<number[] | null>(null);
  const comparedIds = compared ?? roles.slice(0, COMPARED_BY_DEFAULT).map((role) => role.id);
  const columns = phone && shownRole ? [shownRole] : roles.filter((role) => comparedIds.includes(role.id));
  const toggleCompared = (id: number) => {
    const next = comparedIds.includes(id) ? comparedIds.filter((x) => x !== id) : [...comparedIds, id];
    if (next.length > 0) setCompared(next);
  };

  const current = (role: Role) => changes[role.id] ?? new Set(role.permissions);
  const toggle = (role: Role, code: string) => {
    const next = new Set(current(role));
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSaved(false);
    setChanges({ ...changes, [role.id]: next });
  };
  const save = useMutation({
    mutationFn: async () => {
      for (const role of roles) {
        const permissions = changes[role.id];
        if (!permissions || role.is_system) continue;
        await adminApi.updateRole(role.id, { ...roleInput(role), permissions: [...permissions].sort() });
      }
    },
    onSuccess: async () => {
      setChanges({});
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
    },
  });
  const dirty = Object.keys(changes).length > 0;

  return (
    <Card aria-labelledby="matrix-title">
      <CardHeader
        title={<span id="matrix-title">{t("admin.roles.matrix")}</span>}
        actions={
          <Button onClick={() => save.mutate()} busy={save.isPending} disabled={!dirty}>
            {t("admin.roles.saveMatrix")}
          </Button>
        }
      />
      <CardBody className="space-y-3 pb-2">
        <p className="text-sm text-ink-muted">{t("admin.roles.matrixHint")}</p>
        {save.isError && <Alert>{roleError(save.error, t)}</Alert>}
        {saved && <Alert tone="primary">{t("common.saved")}</Alert>}
        {!phone && (
          <div className="space-y-1.5">
            <p id="compared-label" className="text-xs font-medium text-ink-muted">
              {t("admin.roles.compared")}
            </p>
            <div role="group" aria-labelledby="compared-label" className="flex flex-wrap gap-1.5">
              {roles.map((role) => {
                const on = comparedIds.includes(role.id);
                return (
                  <button
                    key={role.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleCompared(role.id)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs font-medium",
                      on
                        ? "border-primary bg-primary-soft text-primary"
                        : "border-line-strong text-ink-muted hover:bg-sunken hover:text-ink",
                    )}
                  >
                    {role.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {phone && shownRole && (
          <SelectField
            label={t("admin.roles.showRole")}
            value={shownRole.id}
            onChange={(e) => setShownRoleId(Number(e.target.value))}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </SelectField>
        )}
      </CardBody>
      <div className="relative overflow-x-auto">
        <table className={phone ? "w-full text-sm" : "w-full min-w-[40rem] text-sm"}>
          <thead className="border-y border-line bg-surface-2 text-xs text-ink-muted">
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-surface-2 px-5 py-3 text-left font-medium">
                <span className="sr-only">{t("admin.roles.matrix")}</span>
              </th>
              {columns.map((role) => (
                <th key={role.id} scope="col" className="px-3 py-3 text-center font-medium">
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.data?.map((group) => (
              <Fragment key={group.code}>
                <tr className="bg-sunken/60">
                  <th
                    colSpan={columns.length + 1}
                    scope="colgroup"
                    className="px-5 py-2 text-left text-xs font-semibold text-ink-muted"
                  >
                    {t(`permissions.groups.${group.code}`)}
                  </th>
                </tr>
                {group.permissions.map((code) => (
                  <tr key={code} className="border-b border-line last:border-b-0 hover:bg-surface-2">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-surface px-5 py-2.5 text-left font-normal text-ink"
                    >
                      {t(`permissions.${code}`)}
                    </th>
                    {columns.map((role) => (
                      <td key={role.id} className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          className="size-4 accent-[var(--tv-primary)]"
                          aria-label={`${role.name}: ${t(`permissions.${code}`)}`}
                          checked={role.is_system || current(role).has(code)}
                          disabled={role.is_system}
                          onChange={() => toggle(role, code)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RoleDialog({ role, open, onClose }: { role: Role | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queues = useQuery({ queryKey: ["admin", "queues"], queryFn: adminApi.queues, enabled: open });
  const catalog = useQuery({
    queryKey: ["admin", "permissions"],
    queryFn: adminApi.permissions,
    enabled: open,
  });
  const [draft, setDraft] = useState<RoleInput>(role ? roleInput(role) : EMPTY_ROLE);
  const togglePermission = (code: string, on: boolean) =>
    setDraft({
      ...draft,
      permissions: on ? [...draft.permissions, code].sort() : draft.permissions.filter((p) => p !== code),
    });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => (role ? adminApi.updateRole(role.id, draft) : adminApi.createRole(draft)),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => adminApi.deleteRole(role?.id as number), onSuccess: done });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !value && onClose()}
      title={role ? t("admin.roles.editTitle") : t("admin.roles.createTitle")}
      wide
      footer={
        <>
          {role && !role.is_system && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmDelete(true)}>
              {t("common.delete")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="role-form" busy={save.isPending}>
            {role ? t("common.save") : t("common.create")}
          </Button>
        </>
      }
    >
      <form id="role-form" onSubmit={onSubmit} className="space-y-6">
        {(save.isError || remove.isError) && <Alert>{roleError(save.error ?? remove.error, t)}</Alert>}
        {confirmDelete && role && (
          <Alert tone="accent">
            <p>{t("admin.roles.deleteConfirm", { name: role.name })}</p>
            <div className="mt-3 flex gap-2">
              <Button variant="danger" onClick={() => remove.mutate()} busy={remove.isPending}>
                {t("common.delete")}
              </Button>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          </Alert>
        )}
        {role?.is_system && <Alert tone="accent">{t("admin.roles.systemLocked")}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("admin.roles.name")}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            required
            autoFocus={!role?.is_system}
            readOnly={role?.is_system}
          />
          <TextField
            label={t("admin.roles.description")}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            readOnly={role?.is_system}
          />
        </div>
        <CheckboxField
          label={t("admin.roles.require2fa")}
          checked={draft.require_2fa}
          onChange={(e) => setDraft({ ...draft, require_2fa: e.target.checked })}
        />
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">{t("admin.roles.permissions")}</h3>
            <p className="text-xs text-ink-subtle">
              {role?.is_system ? t("admin.roles.systemPermissions") : t("admin.roles.permissionsHint")}
            </p>
          </div>
          {!role?.is_system &&
            catalog.data?.map((group) => (
              <fieldset key={group.code} className="m-0 space-y-2 border-0 p-0">
                <legend className="mb-1 text-xs font-semibold text-ink-muted">
                  {t(`permissions.groups.${group.code}`)}
                </legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {group.permissions.map((code) => (
                    <CheckboxField
                      key={code}
                      label={t(`permissions.${code}`)}
                      checked={draft.permissions.includes(code)}
                      onChange={(e) => togglePermission(code, e.target.checked)}
                    />
                  ))}
                </div>
              </fieldset>
            ))}
        </section>
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">{t("admin.roles.scope")}</h3>
            <p className="text-xs text-ink-subtle">
              {role?.is_system ? t("admin.roles.systemScope") : t("admin.roles.scopeHint")}
            </p>
          </div>
          {!role?.is_system && draft.scope_rules.length === 0 && (
            <Alert tone="accent">{t("admin.roles.noQueuesWarning")}</Alert>
          )}
          {!role?.is_system && (
            <ScopeRulesEditor
              rules={draft.scope_rules}
              queues={queues.data ?? []}
              onChange={(scope_rules) => setDraft({ ...draft, scope_rules })}
            />
          )}
        </section>
      </form>
    </Dialog>
  );
}

export function RolesPage() {
  const { t } = useTranslation();
  const roles = useQuery({ queryKey: ["admin", "roles"], queryFn: adminApi.roles });
  const [dialog, setDialog] = useState<{ role: Role | null; key: number } | null>(null);
  const sorted = useMemo(() => roles.data ?? [], [roles.data]);

  return (
    <>
      <PageHeader
        title={t("admin.roles.title")}
        actions={
          <Button onClick={() => setDialog({ role: null, key: Date.now() })}>
            <Plus className="size-4" aria-hidden="true" />
            {t("admin.roles.new")}
          </Button>
        }
      />
      <div className="space-y-6">
        <Card aria-labelledby="roles-title">
          <CardHeader title={<span id="roles-title">{t("admin.roles.list")}</span>} />
          <ul className="divide-y divide-line">
            {sorted.map((role) => (
              <li key={role.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{role.name}</span>
                    {role.is_system && <Badge tone="accent">{t("admin.roles.system")}</Badge>}
                    {role.require_2fa && <Badge tone="primary">2FA</Badge>}
                    {!role.is_system && role.scope_rules.length === 0 && (
                      <Badge tone="danger">{t("admin.roles.noQueues")}</Badge>
                    )}
                  </div>
                  {role.description && <p className="text-sm text-ink-muted">{role.description}</p>}
                </div>
                <span className="text-sm text-ink-subtle tabular-nums">
                  {t("admin.roles.users", { count: role.user_count })}
                </span>
                <Button variant="ghost" onClick={() => setDialog({ role, key: Date.now() })}>
                  {t("common.edit")}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
        {sorted.length > 0 && <PermissionMatrix roles={sorted} />}
      </div>
      {dialog && <RoleDialog key={dialog.key} role={dialog.role} open onClose={() => setDialog(null)} />}
    </>
  );
}
